import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Bot, Send, Loader2, Sparkles, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HermesChat({
  contextHint = "",
  title = "O-Bot",
  icon: Icon = Bot,
  color = "from-indigo-600 to-indigo-700",
  accentColor = "text-indigo-400",
  layout = "full",        // "full" | "sidebar" | "tab" | "dialog"
  height,
  placeholder = "Ask me anything...",
  suggestedQuestions = [],
  sessionPrefix = "hermes",
  showHistory = false,
  showHeader = true,
  onClose,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const fullMessage = contextHint
        ? `${contextHint} ${userMessage}`
        : userMessage;

      const res = await axios.post(`${API}/agent/hermes/chat`,
        { message: fullMessage },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );

      setMessages(prev => [...prev, {
        role: "assistant",
        content: res.data.response,
      }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to get response");
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I apologize, but I encountered an error. Please try again.",
      }]);
    }
    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const containerClass = layout === "sidebar"
    ? "flex flex-col h-full border-l border-slate-200 bg-white"
    : layout === "dialog"
    ? "flex flex-col h-full bg-white rounded-xl shadow-lg overflow-hidden"
    : layout === "tab"
    ? "flex flex-col h-full bg-white"
    : "flex flex-col h-full bg-white rounded-xl border shadow-lg overflow-hidden";

  const headerClass = layout === "sidebar"
    ? `bg-gradient-to-r ${color} px-3 py-2`
    : `bg-gradient-to-r ${color} p-4`;

  const scrollClass = layout === "sidebar"
    ? "flex-1 p-2"
    : "flex-1 p-4";

  return (
    <div className={containerClass} style={height ? { height } : undefined}>
      {/* Header */}
      {showHeader && (
        <div className={headerClass}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon className={`w-4 h-4 text-white ${layout === "sidebar" ? "" : "w-5 h-5"}`} />
              <span className={`font-semibold text-white ${layout === "sidebar" ? "text-xs" : "text-sm"}`}>
                {title}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {onClose && (
                <button onClick={onClose} className="text-white/80 hover:text-white text-xs">
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className={scrollClass} ref={scrollRef}>
        {messages.length === 0 ? (
          <div className={`flex flex-col items-center justify-center text-center ${
            layout === "sidebar" ? "py-4" : "py-12"
          }`}>
            <Sparkles className={`${accentColor} ${
              layout === "sidebar" ? "w-8 h-8 mb-2" : "w-12 h-12 mb-4"
            }`} />
            <p className={`text-slate-500 ${layout === "sidebar" ? "text-xs" : "text-sm"}`}>
              {layout === "sidebar" ? "Ask me anything" : `Ask me about ${contextHint.replace(/[\[\]]/g, '').toLowerCase() || 'anything'}!`}
            </p>
            {suggestedQuestions.length > 0 && layout !== "sidebar" && (
              <div className="grid sm:grid-cols-2 gap-2 w-full max-w-lg mt-4">
                {suggestedQuestions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInput(q.text)}
                    className="flex items-center gap-2 p-3 bg-white border border-slate-200 hover:border-slate-300 transition-colors text-left rounded-lg"
                  >
                    {q.icon && <q.icon className={`w-4 h-4 ${q.color || "text-slate-500"}`} />}
                    <span className="text-xs text-slate-700">{q.text}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className={`space-y-3 ${layout === "sidebar" ? "" : "py-2"}`}>
            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-bubble ${msg.role}`}>
                {msg.role === "assistant" && (
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-3 h-3 ${accentColor}`} />
                    <span className={`text-xs font-semibold ${accentColor}`}>{title.toUpperCase()}</span>
                  </div>
                )}
                <div className={`whitespace-pre-wrap ${layout === "sidebar" ? "text-xs" : "text-sm"} leading-relaxed ${msg.role === "user" ? "text-slate-800" : "text-slate-700"}`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="chat-bubble assistant">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-3 h-3 ${accentColor}`} />
                  <span className={`text-xs font-semibold ${accentColor}`}>{title.toUpperCase()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="thinking-dot" />
                  <div className="thinking-dot" />
                  <div className="thinking-dot" />
                </div>
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className={`border-t border-slate-200 ${layout === "sidebar" ? "p-2" : "p-4"}`}>
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            className={`flex-1 bg-white ${layout === "sidebar" ? "text-xs py-1" : ""}`}
            disabled={isLoading}
          />
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            className={`bg-gradient-to-r ${color} hover:opacity-90 ${layout === "sidebar" ? "h-8 w-8 p-0" : ""}`}
            size={layout === "sidebar" ? "sm" : "default"}
          >
            {isLoading ? (
              <Loader2 className={`animate-spin ${layout === "sidebar" ? "w-3 h-3" : "w-4 h-4"}`} />
            ) : (
              <Send className={layout === "sidebar" ? "w-3 h-3" : "w-4 h-4"} />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
